import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import { useTheme } from "../../contexts/ThemeContext";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://trackly.app";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "name": "About Trackly — Our Mission & Story",
      "description": "Trackly was built by resellers who got tired of bad math. Learn our story, values, and why we built the profit tracker we always wished existed.",
      "url": `${SITE_URL}/about`,
      "isPartOf": {
        "@type": "WebSite",
        "name": "Trackly",
        "url": SITE_URL,
      },
    },
    {
      "@type": "Organization",
      "name": "Trackly",
      "url": SITE_URL,
      "description": "Trackly is a profit tracking platform for sports card flippers and resellers, providing fee-aware margin calculations and performance analytics.",
      "foundingDate": "2025",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "support@trackly.to",
        "contactType": "customer support",
      },
    },
  ],
};

const VALUES = [
  {
    icon: "ri-search-eye-line",
    title: "Radical Transparency",
    body: "Every fee, every margin, every cut — visible. We built Trackly because resellers deserve to see exactly where their money goes.",
  },
  {
    icon: "ri-time-line",
    title: "Built for Speed",
    body: "You shouldn't need a spreadsheet PhD to run your numbers. Trackly gets out of your way so you can focus on the flip.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Privacy First",
    body: "Your sales data is yours. We don't sell it, advertise against it, or share it. Full stop.",
  },
  {
    icon: "ri-community-line",
    title: "Community Driven",
    body: "We listen to resellers, not investors. Every feature on our roadmap came directly from people who actually flip.",
  },
];

const TIMELINE = [
  {
    period: "Late 2024",
    title: "The Problem",
    body: "Our founder was flipping sneakers and realised every margin calculation was being done in a Notes app. Fees were missed, taxes forgotten, profit inflated. There had to be a better way.",
  },
  {
    period: "Early 2025",
    title: "First Build",
    body: "A rough internal tool was shared with a small group of resellers. Within weeks, 200+ people were using it daily. The feedback was overwhelming — this needed to be a real product.",
  },
  {
    period: "Mid 2025",
    title: "Trackly Takes Shape",
    body: "We rebuilt from scratch with a proper architecture, added support for every major resale platform, and designed an experience that felt as fast as the market moves.",
  },
  {
    period: "2026",
    title: "Early Access",
    body: "Trackly opens to the public in early access. Thousands of resellers across cards, sneakers, clothing, and collectibles are now tracking their margins with real precision.",
  },
];

export default function About() {
  const { isDark } = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <SEOHead
        title="About Trackly — Our Mission & Story | Reseller Profit Tracker"
        description="Trackly was built by resellers who got tired of bad math. Learn our story, values, and why we built the profit tracker we always wished existed."
        keywords="about trackly, reseller profit tracker story, flip margin calculator team, trackly mission"
        canonical={`${SITE_URL}/about`}
        ogTitle="About Trackly — Built by Resellers, for Resellers"
        ogDescription="We got tired of missing fees and inflated margins. Trackly is the tool we always wished existed."
        jsonLd={aboutJsonLd}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-36 pb-24 px-8 lg:px-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className={`inline-flex items-center gap-2 font-inter text-xs font-medium px-3 py-1.5 rounded-full mb-7 ${isDark ? "bg-white/5 text-[#888]" : "bg-black/6 text-[#666]"}`}>
              <i className="ri-team-line text-xs" />
              Our Story
            </span>
            <h1 className={`font-syne text-5xl lg:text-6xl font-bold mb-6 leading-tight ${isDark ? "text-white" : "text-[#111111]"}`}>
              We&apos;re resellers<br />
              <span className={isDark ? "text-[#444]" : "text-[#cccccc]"}>who got tired of bad math.</span>
            </h1>
            <p className={`font-inter text-base leading-relaxed max-w-xl mx-auto ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
              Trackly was born out of frustration with spreadsheets, forgotten fees, and profits that looked great until taxes hit. We built the tool we always wished existed.
            </p>
          </div>
        </section>

        {/* Mission Banner */}
        <section className={`py-16 px-8 lg:px-16 border-y transition-colors duration-300 ${isDark ? "border-white/5" : "border-black/6"}`}>
          <div className="max-w-3xl mx-auto text-center">
            <p className={`font-syne text-2xl lg:text-3xl font-semibold leading-relaxed ${isDark ? "text-white" : "text-[#111111]"}`}>
              &ldquo;Know your real margin on every single flip — before you commit, not after.&rdquo;
            </p>
            <p className={`font-inter text-sm mt-4 ${isDark ? "text-[#555]" : "text-[#888]"}`}>
              — The Trackly Mission
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 px-8 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <span className={`font-inter text-xs uppercase tracking-[0.2em] block mb-4 ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>
                What We Stand For
              </span>
              <h2 className={`font-syne text-4xl font-bold ${isDark ? "text-white" : "text-[#111111]"}`}>
                Our values
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {VALUES.map((v) => (
                <div
                  key={v.title}
                  className={`rounded-2xl border p-8 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-5 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                    <i className={`${v.icon} text-lg ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
                  </div>
                  <h3 className={`font-syne text-lg font-semibold mb-3 ${isDark ? "text-white" : "text-[#111111]"}`}>
                    {v.title}
                  </h3>
                  <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                    {v.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className={`py-24 px-8 lg:px-16 border-t transition-colors duration-300 ${isDark ? "border-white/5" : "border-black/6"}`}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <span className={`font-inter text-xs uppercase tracking-[0.2em] block mb-4 ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>
                How We Got Here
              </span>
              <h2 className={`font-syne text-4xl font-bold ${isDark ? "text-white" : "text-[#111111]"}`}>
                The journey
              </h2>
            </div>
            <div className="relative flex flex-col gap-0">
              {TIMELINE.map((item, i) => (
                <div key={item.period} className="flex gap-8">
                  {/* Spine */}
                  <div className="flex flex-col items-center flex-shrink-0 w-6">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${isDark ? "bg-white/30" : "bg-black/25"}`} />
                    {i < TIMELINE.length - 1 && (
                      <div className={`w-px flex-1 mt-1 mb-0 min-h-[40px] ${isDark ? "bg-white/6" : "bg-black/8"}`} />
                    )}
                  </div>
                  {/* Content */}
                  <div className="pb-12">
                    <span className={`font-inter text-xs font-medium uppercase tracking-widest ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
                      {item.period}
                    </span>
                    <h3 className={`font-syne text-xl font-semibold mt-1.5 mb-3 ${isDark ? "text-white" : "text-[#111111]"}`}>
                      {item.title}
                    </h3>
                    <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-24 px-8 lg:px-16 border-t transition-colors duration-300 ${isDark ? "border-white/5" : "border-black/6"}`}>
          <div className="max-w-xl mx-auto text-center">
            <h2 className={`font-syne text-4xl font-bold mb-5 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Ready to see your real margins?
            </h2>
            <p className={`font-inter text-sm leading-relaxed mb-8 ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
              Join thousands of resellers who track every flip with precision.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/product#pricing"
                className={`font-inter text-sm font-medium px-6 py-3 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111111] text-white hover:bg-[#333333]"}`}
              >
                View Plans
              </Link>
              <Link
                to="/demonstration"
                className={`font-inter text-sm px-6 py-3 rounded-full border transition-colors duration-200 cursor-pointer whitespace-nowrap ${isDark ? "border-white/15 text-[#888] hover:text-white hover:border-white/30" : "border-black/15 text-[#888] hover:text-[#111] hover:border-black/30"}`}
              >
                See a Demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
